import { useState, useEffect } from "react";
import "./App.css";
import { NewRoom } from "./NewRoom";
import { Tutorial } from "./Pages/Tutorial/Tutorial";
import { Background } from "./Components/Background";
import Menu from "./Pages/Menu";
import StartPage from "./Pages/StartPage";
import CreateRoom from "./Pages/CreateRoom";
import JoinRoom from "./Pages/JoinRoom";

import socketIO from "socket.io-client";

import i18n from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "./i18n";

import { InfoMessage } from "./Components/InfoMessage/InfoMessage";

import useThemeColor from "./hooks/TopBarColorWheel";
import useContainerHeight from "./hooks/useContainerHeight";
import useScrollTrap from "./hooks/useScrollTrap";
import useIsMobile from "./hooks/useIsMobile";
import AIRoom from "./AIRoom.tsx";
import { useSessionId } from "./hooks/useSessionId.ts";

const TOP_INSET = 66;
const BOTTOM_INSET = 107;

const API_BASE = process.env.REACT_APP_API_BASE;

const socket = socketIO.connect(API_BASE, {
  transports: ["websocket", "polling", "flashsocket"],
});

const fetchString = `${API_BASE}/api/`;

export function StartScreen() {
  useThemeColor();
  const containerHeight = useContainerHeight();
  const [sessionId, resetSessionId] = useSessionId();

  const [roomName, setRoomName] = useState(""); // todo this is not needed can use roomInput
  const [roomState, setRoomState] = useState("start screen");

  const isMobile = useIsMobile();
  const isStartScreen = roomState === "start screen";
  useScrollTrap(isStartScreen);
  const [openJoin, setopenJoin] = useState(false);
  const [openCreateState, setOpenCreateState] = useState("");

  const [AIRoomState, setAIRoomState] = useState("name-entry");

  const [openMenu, setopenMenu] = useState(false);
  const [roomInput, setRoomInput] = useState("");

  const [shouldShow, setShouldShow] = useState(false);
  const [color, setColor] = useState("");
  const [text, setText] = useState("");
  const [add, setAdd] = useState(false);

  function showInfoMessage(
    type,
    message,
    isAdd,
    duration = "medium",
    isDelay = false,
  ) {
    const getDurationMs = (duration) => {
      switch (duration) {
        case "short":
          return 2500;
        case "medium":
          return 5000;
        case "long":
          return 20000;
        default:
          return 5000; // fallback to medium
      }
    };

    const durationMs = getDurationMs(duration);

    setAdd(!!isAdd);
    setColor(type === "warning" ? "#FF4D00" : "#F42272");

    const show = () => {
      setText(message);
      setShouldShow(true);
      setTimeout(() => setShouldShow(false), durationMs);
    };

    if (isDelay) {
      setTimeout(show, 2000); // Delay before showing the message
    } else {
      show();
    }
  }

  const [firstTime, setFirstTime] = useState(() => {
    const saved = localStorage.getItem("firstTime");
    return saved || "true";
  });

  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;
    const resolved = i18n.resolvedLanguage || i18n.language;
    if (resolved && SUPPORTED_LANGUAGES.includes(resolved)) return resolved;
    return "nb-NO";
  });

  const { t, i18n } = useTranslation();

  const [isMenuAnimating, setIsMenuAnimating] = useState(false);

  // Override root background to black when menu is fully open
  // so the animation doesn't show above/below the menu.
  // Delay 500ms on open (wait for slide-in animation).
  // Clear immediately when close animation starts (isMenuAnimating = true).
  useEffect(() => {
    if (openMenu && !isMenuAnimating) {
      const t = setTimeout(() => {
        document.documentElement.style.backgroundColor = "black";
        document.body.style.backgroundColor = "black";
      }, 500);
      return () => clearTimeout(t);
    } else {
      document.documentElement.style.backgroundColor = "";
      document.body.style.backgroundColor = "";
    }
  }, [openMenu, isMenuAnimating]);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isRightSwipe) setopenMenu(true);
    if (isLeftSwipe) {
      setIsMenuAnimating(true);
      setTimeout(() => {
        setIsMenuAnimating(false);
        setopenMenu(false);
      }, 500); // Adjust the timeout based on your animation duration
    }
  };

  useEffect(() => {
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);

    localStorage.setItem("firstTime", firstTime);
  }, [language, firstTime]);

  useEffect(() => {
    const inRoom = roomState === "classic room" || roomState === "ai room";
    if (!inRoom) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [roomState]);

  const onInputChange = (e) => {
    const input = e.currentTarget.value;
    if (/^[^!-\/:-@\[-`{-~]+$/.test(input) || input === "") {
      setRoomInput(input);
    }
  };

  const joinRoom = () => {
    if (roomInput !== "") {
      canJoin();
    }
    document.getElementById("page").scrollIntoView();
  };

  const canJoin = async () => {
    const normalized = roomInput.toLowerCase().trim();
    const classicRoom = await fetchClassicRoom(normalized);
    if (classicRoom) {
      setRoomName(normalized);
      setRoomState("classic room");
      setopenJoin(false);
      return;
    }
    const aiRoom = await fetchAIRoom(normalized);
    if (aiRoom) {
      setRoomName(normalized);
      const alreadyJoined = (aiRoom.players || []).some(
        (p) => p.sessionId === sessionId && p.name,
      );
      setAIRoomState(alreadyJoined ? "lobby" : "name-entry");
      setRoomState("ai room");
      setopenJoin(false);
      return;
    }
    showInfoMessage("warning", t("no-exist"), false);
  };

  const fetchClassicRoom = async (roomName) => {
    const data = await fetch(fetchString + "classic/" + roomName, {
      headers: { Accept: "application/json" },
    }).then((res) => res.json());
    return JSON.stringify(data) !== "{}";
  };

  const fetchAIRoom = async (roomName) => {
    const data = await fetch(fetchString + "ai/" + roomName, {
      headers: { Accept: "application/json" },
    }).then((res) => res.json());
    return data.data || null;
  };

  const idIsFree = async (input) => {
    const classic = await fetchClassicRoom(input);
    if (classic) return false;
    const ai = await fetchAIRoom(input);
    return !ai;
  };

  const createRoom = async () => {
    if (roomInput !== "") {
      const free = await idIsFree(roomInput.toLowerCase().trim());
      if (free) {
        // socket.emit(
        //   "create " + openCreateState,
        //   roomInput.toLowerCase().trim(),
        // );
        // setRoomName(roomInput.toLowerCase().trim());
        // setRoomState(openCreateState);
        // setOpenCreateState("");

        if (roomInput.toLowerCase().trim().startsWith("ai ")) {
          socket.emit("create ai room", roomInput.toLowerCase().trim());
          setRoomName(roomInput.toLowerCase().trim());
          setRoomState("ai room");
          setAIRoomState("name-entry");
          setOpenCreateState("");
        } else {
          socket.emit(
            "create " + openCreateState,
            roomInput.toLowerCase().trim(),
          );
          setRoomName(roomInput.toLowerCase().trim());
          setRoomState(openCreateState);
          setOpenCreateState("");
        }
      } else showInfoMessage("warning", t("taken"), false);
    }
    document.getElementById("page").scrollIntoView();
  };

  const openJoinFunc = () => {
    setopenJoin(!openJoin);
  };

  const openCreateFunc = (state) => {
    setOpenCreateState(state);
  };

  const closeMenu = () => {
    setIsMenuAnimating(true);
    setTimeout(() => {
      setIsMenuAnimating(false);
      setopenMenu(false);
    }, 500); // Adjust the timeout based on your animation duration
  };

  function renderAppContent() {
    if (roomState === "classic room") {
      return firstTime === "true" ? (
        <Tutorial setFirstTime={setFirstTime} />
      ) : (
        <NewRoom
          socket={socket}
          roomName={roomName.toLowerCase().trim()}
          setRoomState={setRoomState}
          containerHeight={containerHeight}
          showInfoMessage={showInfoMessage}
          fetchString={fetchString + "classic/"}
        />
      );
    } else if (roomState === "ai room") {
      return (
        <AIRoom
          roomName={roomName}
          setRoomState={setRoomState}
          AIRoomState={AIRoomState}
          socket={socket}
          setAIRoomState={setAIRoomState}
          containerHeight={containerHeight}
          showInfoMessage={showInfoMessage}
          fetchString={fetchString + "ai/"}
          sessionId={sessionId}
          resetSessionId={resetSessionId}
        />
      );
    }

    return (
      <>
        {openMenu && (
          <Menu
            isMenuAnimating={isMenuAnimating}
            containerHeight={containerHeight}
            setLanguage={setLanguage}
            language={language}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            closeMenu={closeMenu}
            t={t}
          />
        )}

        {openCreateState === "" && !openJoin && (
          <StartPage
            openCreateFunc={openCreateFunc}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            setopenMenu={setopenMenu}
            openJoinFunc={openJoinFunc}
            t={t}
            setAIRoomState={setAIRoomState}
          />
        )}

        {openCreateState !== "" && (
          <CreateRoom
            openCreateFunc={openCreateFunc}
            roomInput={roomInput}
            onInputChange={onInputChange}
            createRoom={createRoom}
            t={t}
          />
        )}

        {openJoin && (
          <JoinRoom
            openJoinFunc={openJoinFunc}
            roomInput={roomInput}
            onInputChange={onInputChange}
            joinRoom={joinRoom}
            t={t}
          />
        )}
      </>
    );
  }

  return (
    <>
      <InfoMessage
        shouldShow={shouldShow}
        color={color}
        text={text}
        add={add}
        setShouldShow={setShouldShow}
      />

      {/* Mobile: overflowX hidden + relative needed for liquid glass scroll trap */}
      {/* Desktop: full overflow hidden to prevent scrolling */}
      <div
        id="page"
        style={
          isMobile
            ? { position: "relative", overflowX: "hidden" }
            : { position: "relative", overflow: "hidden", height: "100vh" }
        }
      >
        {/* Top spacer: only on start screen + mobile for liquid glass hack */}
        {isStartScreen && isMobile && (
          <div style={{ height: TOP_INSET, pointerEvents: "none" }} />
        )}
        <div
          className="App"
          style={
            isStartScreen && isMobile
              ? {
                  height: containerHeight - 44,
                  paddingTop: -44 + TOP_INSET,
                  paddingBottom: 24 + BOTTOM_INSET,
                }
              : { height: containerHeight - 48 }
          }
        >
          {renderAppContent()}
        </div>

        <Background roomState={roomState} containerHeight={containerHeight} />
      </div>
    </>
  );
}
